import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";

import { FormGroup, Input, showSnackbar } from "@cofano/cck";

import { FormDialog } from "components/dialogs/Dialogs";
import { FormErrorMessage } from "components/form/FormErrorMessage";

import { adminSupportEntrySchema, AdminSupportEntrySchemaInput, AdminSupportEntrySchemaOutput } from "view/admin/utils/admin.schema";
import { AdminSupportEntry } from "view/admin/utils/admin.types";

import AdminSupportCategorySelect from "../selects/AdminSupportCategorySelect";

import { SaveAdminFaqEntryDocument } from "__generated__/view/admin/graphql/admin.mutations.generated";

interface SupportEntryCreateEditDialogProps {
	open: boolean;
	entry: AdminSupportEntry | null;
	onClose: () => void;
	refetch: () => void;
}

const SupportEntryCreateEditDialog = ({ open, entry, onClose, refetch }: SupportEntryCreateEditDialogProps) => {
	const [ saveAdminFaqEntry, { loading } ] = useMutation(SaveAdminFaqEntryDocument);

	const { handleSubmit, control, reset, formState } = useForm<AdminSupportEntrySchemaInput, unknown, AdminSupportEntrySchemaOutput>({
		resolver: zodResolver(adminSupportEntrySchema),
		defaultValues: {
			id: null,
			category: null,
			question: "",
			answer: "",
			sequenceNumber: 0,
		},
	});

	const save = (data: AdminSupportEntrySchemaOutput) => {
		saveAdminFaqEntry({
			variables: {
				input: {
					id: data.id,
					question: data.question,
					answer: data.answer,
					category: data.category?.id,
					sequenceNumber: data.sequenceNumber,
				},
			},
		}).then(() => {
			showSnackbar(intl.formatMessage({ id: "SUCCESSFUL_SAVED" }), { variant: "success" });
			onClose();
			refetch();
		});
	};

	useEffect(() => {
		if (open && entry) {
			reset({
				id: entry.id ?? null,
				category: entry.category ? { id: entry.category.id ?? 0, name: entry.category.name ?? "" } : null,
				question: entry.question ?? "",
				answer: entry.answer ?? "",
				sequenceNumber: entry.sequenceNumber ?? null,
			});
		} else if (open) {
			reset({
				id: null,
				category: null,
				question: "",
				answer: "",
				sequenceNumber: 0,
			});
		}
	}, [ open, entry ]);

	return (
		<FormDialog
			open={open}
			onClose={onClose}
			title={intl.formatMessage({ id: "ADMIN_FAQ_ENTRY_TITLE" })}
			form="adminFaqEntryForm"
			loading={loading}>
			<form onSubmit={(event) => void handleSubmit(save)(event)} id="adminFaqEntryForm">
				<FormGroup
					label={intl.formatMessage({ id: "ADMIN_FAQ_ENTRY_CATEGORY_LABEL" })}
					labelPlacement="left-center"
					overflow="hidden"
					required>
					<Controller
						name="category"
						control={control}
						render={({ field }) => (
							<React.Fragment>
								<AdminSupportCategorySelect {...field} autoFocus />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>

				<FormGroup
					label={intl.formatMessage({ id: "ADMIN_FAQ_ENTRY_QUESTION_LABEL" })}
					labelPlacement="left-center"
					overflow="hidden"
					required>
					<Controller
						name="question"
						control={control}
						render={({ field }) => (
							<React.Fragment>
								<Input {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>

				<FormGroup
					label={intl.formatMessage({ id: "ADMIN_FAQ_ENTRY_ANSWER_LABEL" })}
					labelPlacement="left-center"
					overflow="hidden"
					required>
					<Controller
						name="answer"
						control={control}
						render={({ field }) => (
							<React.Fragment>
								<Input {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			</form>
		</FormDialog>
	);
};

export default SupportEntryCreateEditDialog;
